import { CollapsibleFilters } from '../common/CollapsibleFilters';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  Table,
  Typography,
  Button,
  IconButton,
  Chip,
  Input,
  LinearProgress,
  Alert,
  Tabs,
  TabList,
  Tab,
  TabPanel,
} from '@mui/joy';
import {
  Plus as AddIcon,
  PencilSimple as EditIcon,
  Trash as DeleteIcon,
  MagnifyingGlass as SearchIcon,
  LinkSimple as LinkIcon,
  Tag as CategoryIcon,
  Ruler as StraightenIcon,
  Stack as LayersIcon,
} from '@phosphor-icons/react';
import {
  materialCategoriesApi,
  materialSpecsApi,
  standardMaterialsApi,
  type MaterialCategory,
  type MaterialSpecification,
  type StandardMaterial,
} from '../../lib/distributorsApi';
import { useNotification } from '../../contexts/NotificationContext';
import MaterialCategoryForm from './MaterialCategoryForm';
import { MATERIAL_UNIT_TYPES } from './materialConstants';
import MaterialSpecForm from './MaterialSpecForm';
import StandardMaterialForm from './StandardMaterialForm';
import MaterialMapModal from './MaterialMapModal';

const unitTypeLabel = (value: string) =>
  MATERIAL_UNIT_TYPES.find((u) => u.value === value)?.label || value;

// ---- Categories section ----
const CategoriesSection: React.FC = () => {
  const { showSuccess, showError } = useNotification();
  const [items, setItems] = useState<MaterialCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<MaterialCategory | null>(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await materialCategoriesApi.list({ search: search || undefined });
      setItems(response?.results || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to load material categories');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (item: MaterialCategory) => {
    if (!window.confirm(`Delete category "${item.name}"? This may affect related specifications.`)) {
      return;
    }
    try {
      await materialCategoriesApi.delete(item.id);
      showSuccess('Material category deleted');
      load();
    } catch (err: any) {
      showError(err.response?.data?.detail || 'Failed to delete category');
    }
  };

  return (
    <Card>
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography level="h4">Material Categories</Typography>
          <Button
            startDecorator={<AddIcon />}
            onClick={() => {
              setEditing(null);
              setShowForm(true);
            }}
          >
            Add Category
          </Button>
        </Box>

        <CollapsibleFilters><Input
          placeholder="Search categories..."
          startDecorator={<SearchIcon />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
        /></CollapsibleFilters>

        {error && (
          <Alert color="danger" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading && <LinearProgress />}
        {!loading && items.length === 0 && (
          <Alert color="neutral">No material categories found. Click "Add Category" to create one.</Alert>
        )}

        {!loading && items.length > 0 && (
          <Box sx={{ overflowX: 'auto' }}>
            <Table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Unit Type</th>
                  <th>Order</th>
                  <th>Specs</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <Typography level="body-md" fontWeight="lg">
                        {item.name}
                      </Typography>
                      {item.description && (
                        <Typography level="body-xs" color="neutral">
                          {item.description}
                        </Typography>
                      )}
                    </td>
                    <td>{unitTypeLabel(item.unit_type)}</td>
                    <td>{item.display_order}</td>
                    <td>{item.specifications_count ?? 0}</td>
                    <td>
                      <Chip color={item.is_active ? 'success' : 'neutral'} size="sm">
                        {item.is_active ? 'Active' : 'Inactive'}
                      </Chip>
                    </td>
                    <td>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                          size="sm"
                          variant="outlined"
                          onClick={() => {
                            setEditing(item);
                            setShowForm(true);
                          }}
                          title="Edit"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="sm"
                          variant="outlined"
                          color="danger"
                          onClick={() => handleDelete(item)}
                          title="Delete"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Box>
        )}
      </Box>

      <MaterialCategoryForm
        open={showForm}
        category={editing}
        onClose={() => setShowForm(false)}
        onSave={load}
      />
    </Card>
  );
};

// ---- Specifications section ----
const SpecificationsSection: React.FC = () => {
  const { showSuccess, showError } = useNotification();
  const [items, setItems] = useState<MaterialSpecification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<MaterialSpecification | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [mappingSpec, setMappingSpec] = useState<MaterialSpecification | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await materialSpecsApi.list({ search: search || undefined });
      setItems(response?.results || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to load specifications');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (item: MaterialSpecification) => {
    if (!window.confirm(`Delete specification "${item.name}"?`)) {
      return;
    }
    try {
      await materialSpecsApi.delete(item.id);
      showSuccess('Material specification deleted');
      load();
    } catch (err: any) {
      showError(err.response?.data?.detail || 'Failed to delete specification');
    }
  };

  return (
    <Card>
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography level="h4">Material Specifications</Typography>
          <Button
            startDecorator={<AddIcon />}
            onClick={() => {
              setEditing(null);
              setShowForm(true);
            }}
          >
            Add Specification
          </Button>
        </Box>

        <CollapsibleFilters><Input
          placeholder="Search specifications..."
          startDecorator={<SearchIcon />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
        /></CollapsibleFilters>

        {error && (
          <Alert color="danger" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading && <LinearProgress />}
        {!loading && items.length === 0 && (
          <Alert color="neutral">
            No material specifications found. Create a category first, then add specifications.
          </Alert>
        )}

        {!loading && items.length > 0 && (
          <Box sx={{ overflowX: 'auto' }}>
            <Table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Unit</th>
                  <th>Coverage</th>
                  <th>Waste %</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <Typography level="body-md" fontWeight="lg">
                        {item.name}
                      </Typography>
                    </td>
                    <td>{item.category_name || '-'}</td>
                    <td>{item.standard_unit}</td>
                    <td>{item.coverage_per_unit}</td>
                    <td>{item.waste_factor_percent}</td>
                    <td>
                      <Chip color={item.is_active ? 'success' : 'neutral'} size="sm">
                        {item.is_active ? 'Active' : 'Inactive'}
                      </Chip>
                    </td>
                    <td>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                          size="sm"
                          variant="outlined"
                          color="primary"
                          onClick={() => setMappingSpec(item)}
                          title="Map to Products"
                        >
                          <LinkIcon />
                        </IconButton>
                        <IconButton
                          size="sm"
                          variant="outlined"
                          onClick={() => {
                            setEditing(item);
                            setShowForm(true);
                          }}
                          title="Edit"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="sm"
                          variant="outlined"
                          color="danger"
                          onClick={() => handleDelete(item)}
                          title="Delete"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Box>
        )}
      </Box>

      <MaterialSpecForm
        open={showForm}
        spec={editing}
        onClose={() => setShowForm(false)}
        onSave={load}
      />
      <MaterialMapModal
        open={!!mappingSpec}
        spec={mappingSpec}
        onClose={() => setMappingSpec(null)}
      />
    </Card>
  );
};

// ---- Standard Materials section ----
const StandardMaterialsSection: React.FC = () => {
  const { showSuccess, showError } = useNotification();
  const [items, setItems] = useState<StandardMaterial[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<StandardMaterial | null>(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await standardMaterialsApi.list({ search: search || undefined });
      setItems(response?.results || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to load standard materials');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (item: StandardMaterial) => {
    if (!window.confirm(`Delete standard material "${item.name}"?`)) {
      return;
    }
    try {
      await standardMaterialsApi.delete(item.id);
      showSuccess('Standard material deleted');
      load();
    } catch (err: any) {
      showError(err.response?.data?.detail || 'Failed to delete standard material');
    }
  };

  return (
    <Card>
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography level="h4">Standard Materials</Typography>
          <Button
            startDecorator={<AddIcon />}
            onClick={() => {
              setEditing(null);
              setShowForm(true);
            }}
          >
            Add Standard Material
          </Button>
        </Box>

        <CollapsibleFilters><Input
          placeholder="Search standard materials..."
          startDecorator={<SearchIcon />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
        /></CollapsibleFilters>

        {error && (
          <Alert color="danger" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading && <LinearProgress />}
        {!loading && items.length === 0 && (
          <Alert color="neutral">No standard materials found. Click "Add Standard Material" to create one.</Alert>
        )}

        {!loading && items.length > 0 && (
          <Box sx={{ overflowX: 'auto' }}>
            <Table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Calculation Key</th>
                  <th>Specification</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <Typography level="body-md" fontWeight="lg">
                        {item.name}
                      </Typography>
                    </td>
                    <td>
                      <Chip size="sm" variant="soft">
                        {item.calculation_key}
                      </Chip>
                    </td>
                    <td>{item.material_spec_name || '-'}</td>
                    <td>
                      <Chip color={item.is_active ? 'success' : 'neutral'} size="sm">
                        {item.is_active ? 'Active' : 'Inactive'}
                      </Chip>
                    </td>
                    <td>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                          size="sm"
                          variant="outlined"
                          onClick={() => {
                            setEditing(item);
                            setShowForm(true);
                          }}
                          title="Edit"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="sm"
                          variant="outlined"
                          color="danger"
                          onClick={() => handleDelete(item)}
                          title="Delete"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Box>
        )}
      </Box>

      <StandardMaterialForm
        open={showForm}
        material={editing}
        onClose={() => setShowForm(false)}
        onSave={load}
      />
    </Card>
  );
};

const MaterialsManager: React.FC = () => {
  const [subTab, setSubTab] = useState(0);

  return (
    <Box>
      <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
        Manage the standardized material catalog used for product mapping and estimating.
      </Typography>

      <Tabs
        value={subTab}
        onChange={(_, value) => setSubTab(value as number)}
        sx={{ bgcolor: 'transparent' }}
      >
        <TabList sx={{ flexWrap: 'wrap', '& > *': { flex: { xs: '1 1 30%', sm: '0 0 auto' } } }}>
          <Tab>
            <CategoryIcon style={{ marginRight: 8 }} />
            Categories
          </Tab>
          <Tab>
            <StraightenIcon style={{ marginRight: 8 }} />
            Specifications
          </Tab>
          <Tab>
            <LayersIcon style={{ marginRight: 8 }} />
            Standard Materials
          </Tab>
        </TabList>

        <TabPanel value={0} sx={{ p: 0, pt: 2 }}>
          <CategoriesSection />
        </TabPanel>
        <TabPanel value={1} sx={{ p: 0, pt: 2 }}>
          <SpecificationsSection />
        </TabPanel>
        <TabPanel value={2} sx={{ p: 0, pt: 2 }}>
          <StandardMaterialsSection />
        </TabPanel>
      </Tabs>
    </Box>
  );
};

export default MaterialsManager;
